// =============================================================================
// AUTH STORE (Zustand)
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../api/client.js';
import { useChatStore } from './chatStore.js';
import { useCalendarStore } from './calendarStore.js';
import { useNotificationStore } from './notificationStore.js';
import { useCallStore } from './callStore.js';
import { usePhotosStore } from './photosStore.js';
import { useMemorySessionStore } from './memorySessionStore.js';

// =============================================================================
// TYPES
// =============================================================================

export interface User {
  id: string;
  email: string | null;
  phone: string | null;
  displayName: string;
  avatarUrl: string | null;
  language: string;
  uiMode: string;
  // Legibility dial (mode-system brief). Account-scoped so it follows the
  // person across devices; App.tsx seeds displayPrefsStore from these on load.
  uiScale: number;
  uiContrast: boolean;
  uiMotion: string;
  hasCompletedOnboarding: boolean;
  isAdmin?: boolean;
}

interface AuthState {
  // State
  user: User | null;
  deviceId: string | null;
  // Whether a session was active (logged in) and should auto-resume on the
  // next cold load. Kept separate from deviceId: deviceId persists across
  // logout for device-row reuse, but must NOT by itself trigger a silent
  // re-login against a surviving httpOnly refresh cookie (P1.4).
  sessionActive: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // 2FA state
  pending2FA: {
    challengeId: string;
    method: 'email' | 'sms';
  } | null;

  // Actions
  login: (email: string | undefined, phone: string | undefined, password: string) => Promise<boolean>;
  verify2FA: (code: string) => Promise<boolean>;
  register: (data: {
    email?: string;
    phone?: string;
    password: string;
    displayName: string;
    language?: string;
    inviteCode?: string;
  }) => Promise<{ success: boolean }>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  clearError: () => void;
  initialize: () => Promise<void>;
  setUser: (user: User) => void;
  completeOnboarding: () => Promise<void>;
  wizardDismissed: boolean;
  dismissWizard: () => void;
}

// =============================================================================
// STORE
// =============================================================================

// Deduplication lock for initialize() - prevents React 18 StrictMode from
// double-calling refreshToken (which rotates the single-use refresh cookie)
let _initPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      deviceId: null,
      sessionActive: false,
      isLoading: false,
      isInitialized: false,
      error: null,
      pending2FA: null,
      wizardDismissed: false,

      // Login
      login: async (email, phone, password) => {
        set({ isLoading: true, error: null });

        // Pass any persisted deviceId so the backend can reuse the
        // existing user_devices row instead of creating a new one. This
        // keeps the IndexedDB Signal session in sync with the server's
        // prekey bundle, which is what makes "logout, log back in" not
        // produce undecryptable placeholder messages for old traffic.
        const persistedDeviceId = get().deviceId || undefined;

        const result = await api.login({
          email: email || undefined,
          phone: phone || undefined,
          password,
          deviceId: persistedDeviceId,
        });

        if (result.error) {
          set({ isLoading: false, error: result.error.message });
          return false;
        }

        if (result.data && 'requires2FA' in result.data && result.data.requires2FA) {
          set({
            isLoading: false,
            pending2FA: {
              challengeId: result.data.challengeId,
              method: result.data.twoFactorMethod,
            },
          });
          return true; // Indicates 2FA is needed
        }

        if (result.data && 'accessToken' in result.data) {
          api.setAccessToken(result.data.accessToken);
          set({
            isLoading: false,
            user: result.data.user,
            deviceId: result.data.deviceId,
            sessionActive: true,
            pending2FA: null,
          });
          return true;
        }

        set({ isLoading: false, error: 'Unexpected response' });
        return false;
      },

      // Verify 2FA
      verify2FA: async (code) => {
        const { pending2FA } = get();
        if (!pending2FA) {
          set({ error: 'No pending 2FA challenge' });
          return false;
        }

        set({ isLoading: true, error: null });

        // Same device-reuse story for the 2FA path.
        const persistedDeviceId = get().deviceId || undefined;
        const result = await api.verify2FA(pending2FA.challengeId, code, persistedDeviceId);

        if (result.error) {
          set({ isLoading: false, error: result.error.message });
          return false;
        }

        if (result.data) {
          api.setAccessToken(result.data.accessToken);
          set({
            isLoading: false,
            user: result.data.user,
            deviceId: result.data.deviceId,
            sessionActive: true,
            pending2FA: null,
          });
          return true;
        }

        set({ isLoading: false, error: 'Unexpected response' });
        return false;
      },

      // Register
      register: async (data) => {
        set({ isLoading: true, error: null });

        const result = await api.register(data);

        if (result.error) {
          set({ isLoading: false, error: result.error.message });
          return { success: false };
        }

        set({ isLoading: false });
        return { success: true };
      },

      // Logout
      logout: async () => {
        set({ isLoading: true });

        await api.logout();
        api.setAccessToken(null);

        // Reset EVERY user-scoped store so the next login on this device
        // starts clean. Logout previously reset only chat + calendar, leaving
        // notifications, call history, the photo library, and memory-session
        // state from user A visible to user B on a shared device.
        useChatStore.getState().reset();
        useCalendarStore.getState().reset();
        useNotificationStore.getState().reset();
        useCallStore.getState().reset();
        usePhotosStore.getState().reset();
        useMemorySessionStore.getState().reset();

        // Drop the service worker's runtime caches. They are keyed by URL
        // only (no auth header), so without this user B is served user A's
        // cached API responses and media bytes. Best-effort: logout must
        // never fail on cache cleanup.
        if (typeof caches !== 'undefined') {
          try {
            await Promise.all([
              caches.delete('api-cache'),
              caches.delete('media-cache'),
            ]);
          } catch {
            // Ignore - Cache Storage may be unavailable (private mode, http).
          }
        }

        // Keep deviceId persisted across logout so the next login on this
        // browser reuses the same user_devices row (the backend scopes reuse
        // to userId, so a different user logging in here still gets a fresh
        // row). Nulling it minted a new "Unknown Device" row on every
        // logout/login cycle (P1.4, decision 1).
        set({
          user: null,
          sessionActive: false,
          isLoading: false,
          pending2FA: null,
        });
      },

      // Refresh session
      refreshSession: async () => {
        const result = await api.refreshToken();

        if (result.error) {
          // Preserve deviceId (see logout): a failed refresh must not orphan
          // the device row, or the next login mints a fresh "Unknown Device".
          // But clear sessionActive so a failed refresh is never silently
          // auto-resumed on the next cold load.
          set({ user: null, sessionActive: false });
          api.setAccessToken(null);
          return false;
        }

        if (result.data) {
          api.setAccessToken(result.data.accessToken);
          return true;
        }

        return false;
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Set user (for updating profile data)
      setUser: (user) => set({ user }),

      // Complete onboarding wizard (persists to DB)
      completeOnboarding: async () => {
        const result = await api.completeOnboarding();
        if (result.data?.success) {
          const user = get().user;
          if (user) {
            set({ user: { ...user, hasCompletedOnboarding: true }, wizardDismissed: false });
          }
        }
      },

      // Dismiss wizard for this session only (not persisted)
      dismissWizard: () => set({ wizardDismissed: true }),

      // Initialize (check existing session)
      initialize: async () => {
        if (get().isInitialized) return;
        // Deduplicate: if already initializing (e.g. StrictMode double-mount), reuse
        if (_initPromise) return _initPromise;

        _initPromise = (async () => {
          try {
            // Only auto-resume when a session was active (not after an explicit
            // logout or a failed refresh). deviceId persists across logout for
            // device-row reuse, so gating on deviceId alone would silently
            // re-login against a surviving httpOnly refresh cookie. Skipping
            // this for fresh/logged-out visitors also avoids a 401 console error.
            const { deviceId, sessionActive } = get();
            if (!deviceId || !sessionActive) {
              set({ isInitialized: true, isLoading: false });
              return;
            }

            set({ isLoading: true });

            // Try to refresh token
            const refreshed = await get().refreshSession();

            if (refreshed) {
              // Fetch user data
              const result = await api.getMe();
              if (result.data) {
                set({
                  user: {
                    id: result.data.id,
                    email: result.data.email,
                    phone: result.data.phone,
                    displayName: result.data.displayName,
                    avatarUrl: result.data.avatarUrl,
                    language: result.data.language,
                    uiMode: result.data.uiMode,
                    uiScale: result.data.uiScale,
                    uiContrast: result.data.uiContrast,
                    uiMotion: result.data.uiMotion,
                    hasCompletedOnboarding: result.data.hasCompletedOnboarding,
                    isAdmin: result.data.isAdmin,
                  },
                  isInitialized: true,
                  isLoading: false,
                  wizardDismissed: false,
                });
                return;
              }
            }

            set({ isInitialized: true, isLoading: false });
          } finally {
            _initPromise = null;
          }
        })();

        return _initPromise;
      },
    }),
    {
      name: 'chlann-auth',
      partialize: (state) => ({
        // Persist deviceId (device-row reuse across logout) and sessionActive
        // (whether to auto-resume on the next cold load). Tokens are in cookies.
        deviceId: state.deviceId,
        sessionActive: state.sessionActive,
      }),
    }
  )
);
